import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../../../components/Navbar';
import Footer from '../../../../../home/components/Footer';

export default function ConsumerUnitUpgradesAbbotsLangley() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // Set page title
    document.title = 'Consumer Unit Upgrades Abbots Langley | Fuse Box Replacement | Beyond Electrical';

    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional consumer unit upgrades in Abbots Langley. NICEIC approved electricians providing 18th Edition compliant fuse box replacements with full certification. Call 020 3198 1818 for same-day service.');
    }

    // Set meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'consumer unit upgrade Abbots Langley, fuse box replacement Abbots Langley, electrician Abbots Langley, 18th edition compliance');
    }

    // Set canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://beyondelectrical.co.uk/services/installations/consumer-unit-upgrades/abbots-langley');
    }

    // Add Schema.org JSON-LD
    const schemaData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'LocalBusiness',
          '@id': 'https://beyondelectrical.co.uk/#organization',
          name: 'Beyond Electrical',
          image: 'https://beyondelectrical.co.uk/logo.png',
          telephone: '020 3198 1818',
          email: 'info@beyondelectrical.co.uk',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Abbots Langley',
            addressLocality: 'Abbots Langley',
            addressRegion: 'Hertfordshire',
            postalCode: 'WD5',
            addressCountry: 'GB'
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 51.7042,
            longitude: -0.4189
          },
          url: 'https://beyondelectrical.co.uk/services/installations/consumer-unit-upgrades/abbots-langley',
          priceRange: '££',
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '08:00',
              closes: '18:00'
            },
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: 'Saturday',
              opens: '09:00',
              closes: '17:00'
            }
          ],
          areaServed: {
            '@type': 'City',
            name: 'Abbots Langley'
          }
        },
        {
          '@type': 'Service',
          '@id': 'https://beyondelectrical.co.uk/services/installations/consumer-unit-upgrades/abbots-langley#service',
          serviceType: 'Consumer Unit Upgrades',
          provider: {
            '@id': 'https://beyondelectrical.co.uk/#organization'
          },
          areaServed: {
            '@type': 'City',
            name: 'Abbots Langley'
          },
          description: 'Professional consumer unit upgrade and fuse box replacement services in Abbots Langley. NICEIC approved electricians providing 18th Edition compliant installations with full certification.',
          offers: {
            '@type': 'Offer',
            availability: 'https://schema.org/InStock',
            priceSpecification: {
              '@type': 'PriceSpecification',
              priceCurrency: 'GBP'
            }
          }
        }
      ]
    };

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemaData);
    document.head.appendChild(scriptTag);

    return () => {
      document.head.removeChild(scriptTag);
    };
  }, []);

  const nearbyAreas = [
    { name: 'Rickmansworth', slug: 'rickmansworth' },
    { name: 'Chorleywood', slug: 'chorleywood' },
    { name: 'Moor Park & Northwood', slug: 'moor-park-northwood' },
    { name: 'Croxley Green', slug: 'croxley-green' },
    { name: 'Pinner', slug: 'pinner' },
    { name: 'Bushey', slug: 'bushey' },
    { name: 'Harrow', slug: 'harrow' },
    { name: 'Ruislip', slug: 'ruislip' },
    { name: 'Stanmore', slug: 'stanmore' },
    { name: 'Edgware', slug: 'edgware' },
    { name: 'Mill Hill', slug: 'mill-hill' },
    { name: 'Radlett', slug: 'radlett' },
    { name: 'Borehamwood', slug: 'borehamwood' },
    { name: 'Elstree', slug: 'elstree' },
    { name: 'Potters Bar', slug: 'potters-bar' },
    { name: 'South Oxhey', slug: 'south-oxhey' }
  ].filter(area => area.slug !== 'abbots-langley');

  const services = [
    {
      icon: 'ri-refresh-line',
      title: 'Fuse Box Replacement',
      description: 'Complete replacement of outdated fuse boxes with modern consumer units featuring enhanced safety features and circuit protection.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'RCD Protection',
      description: 'Installation of residual current devices that instantly cut power when electrical faults are detected, preventing serious injuries.'
    },
    {
      icon: 'ri-file-list-3-line',
      title: '18th Edition Compliance',
      description: 'Ensure your electrical system meets the latest BS 7671 wiring regulations with fully compliant consumer unit installations.'
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Circuit Labeling',
      description: 'Professional labeling of all circuits for easy identification, making future maintenance and emergency shutdowns straightforward.'
    },
    {
      icon: 'ri-award-line',
      title: 'Full Certification',
      description: 'Receive complete electrical installation certificates and compliance documentation for insurance and property records.'
    },
    {
      icon: 'ri-test-tube-line',
      title: 'Electrical Testing',
      description: 'Comprehensive testing of all circuits and safety devices to ensure optimal performance and complete electrical safety.'
    }
  ];

  const whyUpgrade = [
    {
      icon: 'ri-shield-star-line',
      title: 'Enhanced Safety',
      description: 'Modern consumer units provide superior protection against electrical fires, shocks, and other hazards with advanced safety features.'
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Better Protection',
      description: 'RCDs and MCBs offer instant fault detection and circuit isolation, protecting your family and property from electrical dangers.'
    },
    {
      icon: 'ri-file-shield-2-line',
      title: 'Legal Compliance',
      description: 'Meet current electrical regulations and building standards, ensuring your property complies with the latest safety requirements.'
    },
    {
      icon: 'ri-home-heart-line',
      title: 'Insurance Requirements',
      description: 'Many insurance policies require up-to-date electrical systems. An upgraded consumer unit helps maintain valid home insurance.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Property Value',
      description: 'A modern, compliant electrical system increases property value and makes your home more attractive to potential buyers.'
    },
    {
      icon: 'ri-plug-line',
      title: 'Modern Capacity',
      description: 'Handle increased electrical demands from modern appliances, EV chargers, and smart home devices with adequate circuit capacity.'
    }
  ];

  const whyChoose = [
    {
      icon: 'ri-time-line',
      title: '15 Years Experience',
      description: 'Over a decade of expertise in consumer unit upgrades across Abbots Langley and surrounding areas.'
    },
    {
      icon: 'ri-medal-line',
      title: 'NICEIC Approved',
      description: 'Fully qualified and registered electricians ensuring all work meets the highest industry standards.'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Same Day Service',
      description: 'Fast response times with same-day appointments available for urgent consumer unit replacements.'
    },
    {
      icon: 'ri-price-tag-line',
      title: 'Fixed Pricing',
      description: 'Transparent, competitive pricing with no hidden costs. You know exactly what you\'ll pay before we start.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A1628] via-[#0D1D35] to-[#0A1628] text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center gap-3 mb-6 flex-wrap">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/20 text-[#FFD700] text-sm font-medium whitespace-nowrap">
                <i className="ri-shield-check-line mr-2"></i>
                18th Edition Compliant
              </span>
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/20 text-[#FFD700] text-sm font-medium whitespace-nowrap">
                <i className="ri-map-pin-line mr-2"></i>
                Abbots Langley Experts
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Consumer Unit Upgrades in <span className="text-[#FFD700]">Abbots Langley</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Professional fuse box replacement and consumer unit upgrades in Abbots Langley. NICEIC approved electricians providing fully compliant installations with complete certification and same-day service available.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:02031981818"
                className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-[#FFD700] text-[#0A1628] font-semibold hover:bg-[#FFC700] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2 text-xl"></i>
                Call 020 3198 1818
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-white/10 text-white font-semibold hover:bg-white/20 transition-all duration-300 border border-white/20 whitespace-nowrap"
              >
                <i className="ri-chat-quote-line mr-2 text-xl"></i>
                Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Consumer Unit Upgrade Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A1628] mb-4">
              Consumer Unit Upgrade Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive consumer unit upgrade services in Abbots Langley, ensuring your electrical system meets modern safety standards and regulations.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-[#FFD700]/10 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-3xl text-[#FFD700]`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Upgrade Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A1628] mb-4">
              Why Upgrade Your Consumer Unit?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Modern consumer units provide essential safety features and compliance benefits that older fuse boxes simply cannot offer.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyUpgrade.map((reason, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-[#0A1628] to-[#0D1D35] p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-white"
              >
                <div className="w-14 h-14 bg-[#FFD700]/10 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${reason.icon} text-3xl text-[#FFD700]`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3">{reason.title}</h3>
                <p className="text-gray-300 leading-relaxed">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Beyond Electrical */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A1628] mb-4">
              Why Choose Beyond Electrical?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Trusted consumer unit upgrade specialists serving Abbots Langley with professional service and guaranteed quality.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChoose.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center border border-gray-100"
              >
                <div className="w-16 h-16 bg-[#FFD700]/10 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <i className={`${feature.icon} text-3xl text-[#FFD700]`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A1628] mb-4">
              Consumer Unit Upgrades in Nearby Areas
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We also provide professional consumer unit upgrade services throughout the surrounding areas.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {nearbyAreas.map((area, index) => (
              <Link
                key={index}
                to={`/services/installations/consumer-unit-upgrades/${area.slug}`}
                className="px-6 py-3 bg-white border-2 border-[#0A1628] text-[#0A1628] rounded-lg font-semibold hover:bg-[#0A1628] hover:text-white transition-all duration-300 whitespace-nowrap"
              >
                {area.name}
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/services/installations/consumer-unit-upgrades"
              className="inline-flex items-center text-[#FFD700] hover:text-[#FFC700] font-semibold text-lg"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Consumer Unit Upgrades
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#0D1D35] to-[#0A1628] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Upgrade Your Consumer Unit in Abbots Langley?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Contact our NICEIC approved electricians today for a free quote and expert advice on consumer unit upgrades.
          </p>
          <a
            href="tel:02031981818"
            className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-[#FFD700] text-[#0A1628] font-semibold hover:bg-[#FFC700] transition-all duration-300 shadow-lg hover:shadow-xl text-lg whitespace-nowrap"
          >
            <i className="ri-phone-line mr-2 text-xl"></i>
            Call 020 3198 1818 Now
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}